/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ComponentItemFilterMode } from '@gh/builder-shared-data';
import { AbstractFinderDialogComponent } from '@gh/common-internal-shared';
import { EnumDictionary, FormState, FormStateBuilder, getDialogInitData } from '@gh/common-shared';
import { SlicedDataSource } from '@gh/core-data';
import { TranslationService } from '@gh/core-mls';
import { getControlValue } from '@gh/core-ui';
import { assign, isNull, negate, some } from 'lodash';
import {
  ComponentItemFinderBusinessService,
  ComponentItemFinderDialogConfig,
} from '../../resources/component-item-finder-business.service';
import {
  ComponentItemFinderListItem,
  getComponentItemFinderListItemUniqueId,
  isComponentItemFinderId,
} from '../../resources/component-item-finder-list-item.model';
import { ComponentItemFinderGridDefinition } from './component-item-finder-grid-definition.service';

@Component({
  selector: 'gh-component-item-finder-dialog',
  template: require('./component-item-finder-dialog.component.html'),
  styles: [require('./component-item-finder-dialog.component.scss')],
  providers: [ComponentItemFinderGridDefinition],
})
export class ComponentItemFinderDialogComponent
  extends AbstractFinderDialogComponent<ComponentItemFinderDialogConfig, ComponentItemFinderListItem>
  implements OnInit {
  componentItemTypes: EnumDictionary<ComponentItemFilterMode>;
  getId = getComponentItemFinderListItemUniqueId;

  form: FormState<any>;

  defaultTitle = this.translations.get('lbl_find_items_components');

  constructor(private formStateBuilder: FormStateBuilder,
    private translations: TranslationService,
    @Inject(MAT_DIALOG_DATA) private dialogData: any,
    public gridDefinition: ComponentItemFinderGridDefinition,
    private dialogRef: MatDialogRef<any>,
    private businessService: ComponentItemFinderBusinessService) {
    super(dialogRef);
  }

  get disableFilterMode(): any {
    return isNull(this.input.disableFilterMode) ? false : this.input.disableFilterMode;
  }

  itemsCount: Record<string, number> = {};

  getCount(item: ComponentItemFinderListItem): number {
    if(!item) return 0;
    const items = this.selectionModel.getNewEntities();
    const matchedItem = items.find((i: ComponentItemFinderListItem) => i.id === item.id);
    if (!matchedItem) return 0;
    return matchedItem.count ?? 1;
  }

  increaseCount(item: ComponentItemFinderListItem): void {
    const newCount = this.getCount(item) + 1;
    if (newCount === 1) this.onToggle(item)
    this.changeCount(item, newCount);
  }

  decreaseCount(item: any): void {
    const currentCount = this.getCount(item);
    if (currentCount === 0) return;
    const newCount = currentCount - 1;
    if (newCount === 0) this.onToggle(item);
    this.changeCount(item, newCount);
  }

  getTotalCount(): number {
    return this.selectionModel.getTotalCount();
  }

  ngOnInit(): void {
    // validate preselected ids
    if (some(this.input.preselected, negate(isComponentItemFinderId))) {
      throw new Error('ComponentItemFinderDialogComponent: wrong IDs for component/item finder');
    }

    assign(this, getDialogInitData(this.dialogData));
    const type = this.input.filterMode || ComponentItemFilterMode.All;
    this.form = this.formStateBuilder.buildPlain({
      value: { type },
      children: {
        code: void 0,
        type: { enabled: !this.input.disableFilterMode },
      },
    });

    super.ngOnInit();
  }

  protected buildDataSource(): SlicedDataSource<ComponentItemFinderListItem> {
    const code = getControlValue(this.form.group, 'code');
    const type = getControlValue(this.form.group, 'type');

    return this.businessService.findAllAsDataSource(type, code);
  }
}
